import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { glossary } from '@sorare/core/src/lib/glossary';
import { toLanguageCode } from '@sorare/core/src/lib/i18n';
import { getSafeLocaleFromUrl } from '@sorare/core/src/lib/navigation/getSafeLocaleFromUrl';
import { sportToBlogSportMap } from '@sorare/core/src/lib/routing';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { PostExcerpt } from '#src/components/Excerpt/__generated__/index.graphql.ts';
import { Excerpt } from '#src/components/Excerpt/index.tsx';
import { Hero } from '#src/components/Hero/index.tsx';
import { supportedLanguages } from '#src/constants/types.ts';
import { getCategoryVariables } from '#src/lib/getCategoryVariables.ts';
import { blogQueryLoader } from '#src/lib/graphql.ts';

import type {
  getAllPosts,
  getAllPostsVariables,
} from './__generated__/index.graphql.ts';

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5em;

  @media (${tabletAndAbove}) {
    display: grid;
    grid-template-columns: [main-start] 1fr 1fr 1fr [main-end];
  }

  p {
    text-align: justify;
    hyphens: auto;
  }

  figure > img {
    aspect-ratio: 1.8;
    object-fit: cover;
  }
`;

const GET_ALL_POSTS = gql`
  query getAllPosts(
    $first: Int
    $after: String
    $langTag: String
    $search: String
    $categoryId: [ID]
    $sportCategoryName: String
  ) {
    posts(
      first: $first
      after: $after
      where: {
        categoryName: $sportCategoryName
        categoryIn: $categoryId
        tag: $langTag
        search: $search
      }
    ) {
      nodes {
        date
        featured {
          featured
        }
        ...PostExcerpt
        ...HeroPost
      }
      edges {
        cursor
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        endCursor
        startCursor
      }
    }
  }
  ${Excerpt.fragment}
  ${Hero.fragment}
` as TypedDocumentNode<getAllPosts, getAllPostsVariables>;

type Props = {
  isSearching: boolean;
};

function getLanguageCodeFromPathname(pathname: string) {
  const locale = getSafeLocaleFromUrl(pathname);
  return toLanguageCode(locale) || 'en';
}

export const loader = blogQueryLoader<{ category?: string; $sport?: Sport }>(
  GET_ALL_POSTS,
  ({ pathname, searchParams, category, $sport }) => {
    const language = getLanguageCodeFromPathname(pathname);
    const sportCategory = $sport ? sportToBlogSportMap[$sport] : undefined;
    const categoryVariables = getCategoryVariables({ category, sport: $sport });
    return {
      first: 12,
      langTag: supportedLanguages.has(language)
        ? `lang-${language}`
        : 'lang-en',
      search: searchParams.get('search') || '',
      categoryId: categoryVariables.map(c => c.databaseId),
      sportCategoryName: sportCategory,
    };
  }
);

const BlogResultsComponent = ({ isSearching }: Props) => {
  const [showMore, setShowMore] = useState(false);
  const { formatMessage } = useIntl();

  const { data, loadMore } = useQueryLoader<getAllPosts>();

  const posts = useMemo(() => {
    if (!data?.posts?.nodes) return null;

    const { nodes, ...rest } = data.posts;
    const sortedNodes = [...nodes].sort((a, b) => {
      return b.date!.localeCompare(a.date!);
    });

    return {
      ...rest,
      nodes: sortedNodes,
    };
  }, [data]);

  const { InfiniteScrollLoader } = useInfiniteScroll(() => {
    loadMore(
      false,
      {
        after: data?.posts?.pageInfo?.endCursor,
      },
      'RootQueryToPostConnection'
    );
  }, !!data?.posts?.pageInfo?.hasNextPage);

  const [firstThreeFeaturedPosts, otherPosts] = (posts?.nodes ?? []).reduce(
    (acc: PostExcerpt[][], curr: PostExcerpt) => {
      if (curr.featured?.featured === 'true' && acc[0].length < 3) {
        acc[0].push(curr);
      } else {
        acc[1].push(curr);
      }
      return acc;
    },
    [[], []] as PostExcerpt[][]
  );

  return (
    <Vertical style={{ opacity: isSearching ? 0.1 : 1 }}>
      {!!data?.posts?.nodes?.length && (
        <Hero excerpts={firstThreeFeaturedPosts} />
      )}
      {!!otherPosts.length && otherPosts.length > 0 && (
        <Vertical gap={2}>
          <BrandM>{formatMessage(glossary.latest)}</BrandM>
          <Grid>
            {otherPosts.map((post, index: number) => (
              <Excerpt
                key={post.databaseId}
                excerpt={post}
                featured={index === 0}
              />
            ))}
          </Grid>
        </Vertical>
      )}
      {!showMore && (
        <Vertical center>
          <Button
            size="medium"
            color="tertiary"
            onClick={() => setShowMore(true)}
          >
            <FormattedMessage {...glossary.showMore} />
          </Button>
        </Vertical>
      )}
      {!!showMore && <InfiniteScrollLoader />}
    </Vertical>
  );
};

export const BlogResults = (props: Props) => {
  return (
    <Suspense fallback={null}>
      <BlogResultsComponent {...props} />
    </Suspense>
  );
};
